import { useState } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, RotateCcw } from "lucide-react";

const categories = [
  "Notebooks",
  "Pens",
  "Pencils",
  "Erasers",
  "Rulers",
  "Office",
  "Markers",
  "Adhesives",
  "Notes",
  "Filing",
  "Electronics",
  "Home",
  "Other"
];

export default function AddNewItem() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    barcode: "",
    category: "",
    price: "",
    cost: "",
    stock: "100",
    image_url: "",
    is_quick_pick: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFormData({
      name: "",
      barcode: "",
      category: "",
      price: "",
      cost: "",
      stock: "100",
      image_url: "",
      is_quick_pick: false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.barcode || !formData.category || !formData.price) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost) || 0,
        stock: parseInt(formData.stock) || 100
      };

      await axios.post(`${API}/products`, productData, getAuthHeader());
      toast.success(`Product "${formData.name}" added successfully!`);
      handleReset();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add product");
    } finally {
      setLoading(false);
    }
  };

  const generateBarcode = () => {
    const barcode = Math.floor(1000 + Math.random() * 9000).toString();
    handleChange("barcode", barcode);
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="add-item-page">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-bold text-slate-800">Add Product</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {/* Category */}
              <div className="space-y-2">
                <Label>Select Department</Label>
                <Select value={formData.category} onValueChange={(v) => handleChange("category", v)}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select a category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Stock */}
              <div className="space-y-2">
                <Label>Initial Stock</Label>
                <Input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => handleChange("stock", e.target.value)}
                  placeholder="100"
                  data-testid="stock-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Product Name */}
              <div className="space-y-2">
                <Label>Product Name<span className="text-red-500">*</span></Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Enter productName"
                  required
                  data-testid="product-name-input"
                />
              </div>

              {/* Barcode */}
              <div className="space-y-2">
                <Label>UPC / Barcode<span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.barcode}
                    onChange={(e) => handleChange("barcode", e.target.value)}
                    placeholder="Enter UPC"
                    required
                    data-testid="barcode-input"
                  />
                  <Button type="button" variant="outline" onClick={generateBarcode}>
                    Auto
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Cost */}
              <div className="space-y-2">
                <Label>Cost</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => handleChange("cost", e.target.value)}
                  placeholder="Cost"
                  data-testid="cost-input"
                />
              </div>

              {/* Price */}
              <div className="space-y-2">
                <Label>Regular Price<span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  placeholder="Price"
                  required
                  data-testid="price-input"
                />
              </div>
            </div>

            {/* Image URL */}
            <div className="space-y-2">
              <Label>Image URL (Optional)</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => handleChange("image_url", e.target.value)}
                placeholder="https://..."
                data-testid="image-url-input"
              />
            </div>

            {/* Quick Pick */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="quick-pick"
                checked={formData.is_quick_pick}
                onCheckedChange={(checked) => handleChange("is_quick_pick", checked)}
                data-testid="quick-pick-checkbox"
              />
              <Label htmlFor="quick-pick" className="cursor-pointer">
                Add to Quick Pick Items
              </Label>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="submit-product-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="loading-spinner" />
                    Adding...
                  </span>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Submit
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                className="border-orange-500 text-orange-500 hover:bg-orange-50"
                data-testid="reset-form-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
